import { useEffect, useState } from 'react';
import { ErroreApi } from '../api/candidatura.js';
import {
  type Ricerca,
  type Stato,
  ETICHETTE,
  useCambiaPassword,
  useCandidature,
  useChiSono,
  useConteggi,
  useEntra,
  useEsci,
} from '../api/personale.js';
import { Candidatura } from './Candidatura.js';
import { Esportazione } from './Esportazione.js';

/**
 * L'area di chi legge le candidature.
 *
 * Nel v2 è una tabella con un filtro per nome: per sapere quali sono nuove si
 * guarda la data e si tiene a mente, e per sapere se qualcuno l'ha già
 * guardata si chiede in giro. Qui la prima cosa che si vede è **quante ne
 * restano da guardare**, e ogni candidatura porta con sé la sua storia.
 *
 * Il testo è in inglese: lo legge il personale in New Jersey.
 */
export function Personale({ id }: { id: number | null }) {
  const chiSono = useChiSono();

  if (chiSono.isPending) return <div className="pagina-hr attesa">One moment…</div>;
  if (!chiSono.data) return <Accesso />;
  if (id) return <Candidatura id={id} />;
  return <Elenco nome={chiSono.data.name} />;
}

function Accesso() {
  const entra = useEntra();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const errore = entra.error instanceof ErroreApi ? entra.error.message : null;

  return (
    <div className="ingresso">
      <form
        className="ingresso-riquadro"
        onSubmit={(e) => {
          e.preventDefault();
          entra.mutate({ email, password });
        }}
      >
        <div className="marchio">La Regina Atlantica</div>
        <h1>Applications</h1>
        {/* come nel v2: amministratori e HR manager del CRM entrano con le
            loro credenziali, e non devono saperne un'altra */}
        <p className="sottotitolo">
          Sign in with your HR account, or with your CRM e-mail and password.
        </p>
        {errore && (
          <div className="avviso" role="alert">
            {errore}
          </div>
        )}
        <div className="campo">
          <label htmlFor="hr-email">E-mail</label>
          <input
            id="hr-email"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        <div className="campo">
          <label htmlFor="hr-pw">Password</label>
          <input
            id="hr-pw"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>
        <button className="btn btn-avanti" type="submit" disabled={entra.isPending}>
          {entra.isPending ? 'One moment…' : 'Sign in'}
        </button>
      </form>
    </div>
  );
}

/**
 * Quando l'elenco non arriva, lo si dice. Prima restava «Nothing here.»,
 * come se le candidature non ci fossero. Il caso più probabile è il limite
 * di 120 richieste al minuto per indirizzo: chi lavora nello stesso ufficio
 * esce con lo stesso indirizzo, e lo divide.
 */
function elencoNonArrivato(errore: unknown): string {
  if (errore instanceof ErroreApi && errore.stato === 429) {
    return 'Too many requests from this network in the last minute. Please wait a moment and reload the page.';
  }
  return 'The list did not load. Please reload the page in a minute.';
}

function Elenco({ nome }: { nome: string }) {
  const esci = useEsci();
  const [ricerca, setRicerca] = useState<Ricerca>({ q: '', status: '', withResume: false, page: 1 });
  const [esportazioneAperta, setEsportazioneAperta] = useState(false);
  const [passwordAperta, setPasswordAperta] = useState(false);
  const elenco = useCandidature(ricerca);
  const conteggi = useConteggi();

  const cambia = (pezzo: Partial<Ricerca>) => setRicerca((r) => ({ ...r, ...pezzo, page: 1 }));
  const dati = elenco.data;

  return (
    <div className="pagina-hr">
      <header className="testa-hr">
        <div>
          <div className="marchio">La Regina Atlantica</div>
          <h1>Applications</h1>
        </div>
        <div className="testa-destra">
          <span className="tenue">{nome}</span>
          <button className="btn" onClick={() => setEsportazioneAperta(true)}>
            Export to Excel
          </button>
          <button className="btn" onClick={() => setPasswordAperta(true)}>
            Change password
          </button>
          <button className="btn" onClick={() => esci.mutate()}>
            Sign out
          </button>
        </div>
      </header>

      {/* la domanda vera — quante ne devo ancora guardare — è la prima cosa */}
      <div className="stati">
        <button
          className={ricerca.status === '' ? 'stato stato-scelto' : 'stato'}
          onClick={() => cambia({ status: '' })}
        >
          All
          <b>{(conteggi.data?.counts ?? []).reduce((s, c) => s + c.n, 0)}</b>
        </button>
        {(conteggi.data?.counts ?? []).map((c) => (
          <button
            key={c.chiave}
            className={ricerca.status === c.chiave ? 'stato stato-scelto' : 'stato'}
            onClick={() => cambia({ status: c.chiave })}
          >
            {c.etichetta}
            <b>{c.n}</b>
          </button>
        ))}
      </div>

      <div className="filtri">
        <input
          type="search"
          placeholder="Search by number, name, e-mail, position or city"
          value={ricerca.q}
          onChange={(e) => cambia({ q: e.target.value })}
        />
        <label className="casella">
          <input
            type="checkbox"
            checked={ricerca.withResume}
            onChange={(e) => cambia({ withResume: e.target.checked })}
          />
          <span>With a resume</span>
        </label>
      </div>

      <div className="tabella-hr">
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Name</th>
              <th>Position</th>
              <th>Where</th>
              <th>Received</th>
              <th>Resume</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {(dati?.rows ?? []).length === 0 && (
              <tr>
                <td colSpan={7} className="vuoto">
                  {elenco.isFetching
                    ? 'One moment…'
                    : elenco.isError
                      ? elencoNonArrivato(elenco.error)
                      : 'Nothing here.'}
                </td>
              </tr>
            )}
            {(dati?.rows ?? []).map((r) => (
              <tr
                key={r.id}
                onClick={(e) => {
                  // sul nome apre già il link: anche in un'altra scheda, se si chiede
                  if ((e.target as HTMLElement).closest('a')) return;
                  location.href = `/staff/${r.id}`;
                }}
              >
                {/* il numero è quello dell'indirizzo e della scheda: si cerca
                    anche così, «323» o «#323» */}
                <td className="num tenue">#{r.id}</td>
                <td>
                  {/* il nome è un link vero: la riga intera si apriva solo col
                      mouse, e da tastiera non si arrivava a nessuna candidatura */}
                  <a className="riga-link" href={`/staff/${r.id}`}>
                    <strong>{`${r.lastName} ${r.firstName}`.trim() || `Application #${r.id}`}</strong>
                  </a>
                  <br />
                  <span className="tenue">{r.email || r.phone || '—'}</span>
                </td>
                <td>{r.positionDesired || '—'}</td>
                <td>{[r.city, r.state].filter(Boolean).join(', ') || '—'}</td>
                <td className="num">
                  {r.submittedAt ? new Date(r.submittedAt).toLocaleDateString('en-US') : '—'}
                </td>
                <td>
                  {/* zero curriculum si vede a colpo d'occhio: nel v2 non si
                      vedeva, ed è così che nove mesi di candidature sono
                      arrivate senza che nessuno se ne accorgesse */}
                  {r.resumeCount > 0 ? (
                    <span className="pastiglia pastiglia-ok">yes</span>
                  ) : (
                    <span className="pastiglia">no</span>
                  )}
                </td>
                <td>
                  <span className={`pastiglia stato-${r.status}`}>{ETICHETTE[r.status]}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {esportazioneAperta && (
        <Esportazione ricerca={ricerca} onChiudi={() => setEsportazioneAperta(false)} />
      )}

      {passwordAperta && <CambioPassword onChiudi={() => setPasswordAperta(false)} />}

      {dati && dati.pages > 1 && (
        <div className="piede-hr">
          <button
            className="btn"
            disabled={dati.page <= 1}
            onClick={() => setRicerca((r) => ({ ...r, page: r.page - 1 }))}
          >
            Previous
          </button>
          <span className="tenue">
            Page {dati.page} of {dati.pages} · {dati.total} applications
          </span>
          <button
            className="btn"
            disabled={dati.page >= dati.pages}
            onClick={() => setRicerca((r) => ({ ...r, page: r.page + 1 }))}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

export type { Stato };

/**
 * Cambiarsi la password.
 *
 * Finché non c'era, la password di un'utenza la sapeva sempre anche chi
 * l'aveva creata: la prima gliela dà qualcun altro, e non c'era modo di
 * sostituirla. Qui si cambia da soli, serve quella di adesso — una sessione
 * lasciata aperta non deve poter cambiare le chiavi di casa — e le altre
 * sessioni si chiudono.
 */
function CambioPassword({ onChiudi }: { onChiudi: () => void }) {
  const cambia = useCambiaPassword();
  const [attuale, setAttuale] = useState('');
  const [nuova, setNuova] = useState('');
  const [ripetuta, setRipetuta] = useState('');

  // Esc chiude, come la finestra dell'esportazione
  useEffect(() => {
    const tasto = (e: KeyboardEvent) => e.key === 'Escape' && onChiudi();
    document.addEventListener('keydown', tasto);
    return () => document.removeEventListener('keydown', tasto);
  }, [onChiudi]);

  const nonCombaciano = ripetuta !== '' && nuova !== ripetuta;
  const errore = cambia.error instanceof ErroreApi ? cambia.error.message : null;
  const pronto = attuale !== '' && nuova.length >= 8 && nuova === ripetuta;

  if (cambia.isSuccess) {
    return (
      <div className="velo" role="dialog" aria-modal="true" aria-label="Change password">
        <div className="finestra finestra-stretta">
          <header className="finestra-testa">
            <h2>Password changed</h2>
          </header>
          <div className="finestra-corpo">
            <p>
              Your password has been changed. Any other session signed in with the old password has
              been closed; this one stays open.
            </p>
          </div>
          <footer className="finestra-piede">
            <span />
            <div className="finestra-azioni">
              <button className="btn btn-avanti" onClick={onChiudi}>
                Done
              </button>
            </div>
          </footer>
        </div>
      </div>
    );
  }

  return (
    <div className="velo" role="dialog" aria-modal="true" aria-label="Change password">
      <form
        className="finestra finestra-stretta"
        onSubmit={(e) => {
          e.preventDefault();
          if (pronto) cambia.mutate({ currentPassword: attuale, newPassword: nuova });
        }}
      >
        <header className="finestra-testa">
          <h2>Change password</h2>
          <button type="button" className="btn-chiudi" onClick={onChiudi} aria-label="Close">
            ✕
          </button>
        </header>

        <div className="finestra-corpo">
          {errore && (
            <div className="avviso" role="alert">
              {errore}
            </div>
          )}

          <div className="campo">
            <label htmlFor="pw-attuale">Current password</label>
            <input
              id="pw-attuale"
              type="password"
              autoComplete="current-password"
              value={attuale}
              onChange={(e) => setAttuale(e.target.value)}
            />
          </div>

          <div className="campo">
            <label htmlFor="pw-nuova">New password</label>
            <input
              id="pw-nuova"
              type="password"
              autoComplete="new-password"
              value={nuova}
              onChange={(e) => setNuova(e.target.value)}
            />
            <small className="tenue">At least 8 characters.</small>
          </div>

          <div className={nonCombaciano ? 'campo campo-sbagliato' : 'campo'}>
            <label htmlFor="pw-ripetuta">Repeat new password</label>
            <input
              id="pw-ripetuta"
              type="password"
              autoComplete="new-password"
              value={ripetuta}
              onChange={(e) => setRipetuta(e.target.value)}
            />
            {nonCombaciano && <small>The two passwords are different.</small>}
          </div>
        </div>

        <footer className="finestra-piede">
          <span className="tenue">Other sessions will be signed out.</span>
          <div className="finestra-azioni">
            <button type="button" className="btn" onClick={onChiudi}>
              Cancel
            </button>
            <button type="submit" className="btn btn-avanti" disabled={!pronto || cambia.isPending}>
              {cambia.isPending ? 'Changing…' : 'Change password'}
            </button>
          </div>
        </footer>
      </form>
    </div>
  );
}
